import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { createProduct, listBaseUnits, listPackageTypes } from "../services/product";
import WizardSteps from "./shared/WizardSteps";

const TOTAL_STEPS = 2;

const initialState = {
  name: "",
  baseUnitId: "",
  packageTypeId: "",
  unitPrice: "",
  packagePrice: "",
  quantityPerPackage: "",
};

// Split out of the former ProdutosLayer (which mixed the wizard with the
// product table). Mirrors sge_infantil_web/config/config.ts, where
// `create.product` (/expenses/product/create) carries `hideInMenu: true` and
// is only reachable via the "Novo Produto" button on the list screen — see
// ListaProdutosLayer.
const CadastrarProdutoLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [baseUnits, setBaseUnits] = useState([]);
  const [packageTypes, setPackageTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const stepLabels = [t("produto.wizard.step.data"), t("produto.wizard.step.confirm")];

  useEffect(() => {
    listBaseUnits().then((resp) => setBaseUnits(resp.data || [])).catch(() => setBaseUnits([]));
    listPackageTypes().then((resp) => setPackageTypes(resp.data || [])).catch(() => setPackageTypes([]));
  }, []);

  const unitName = (id) => baseUnits.find((u) => u.id === id)?.name || "-";
  const packageName = (id) => packageTypes.find((p) => p.id === id)?.name || "-";

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const nextStep = () => {
    if (!form.name || !form.baseUnitId) {
      toast.error(t("produto.error.requiredFields"));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const baseUnit = baseUnits.find((u) => u.id === form.baseUnitId);
      const packageType = packageTypes.find((p) => p.id === form.packageTypeId);
      await createProduct({
        name: form.name,
        baseUnitId: form.baseUnitId,
        baseUnit: baseUnit ? { id: baseUnit.id, name: baseUnit.name } : undefined,
        packageTypeId: form.packageTypeId || undefined,
        packageType: packageType ? { id: packageType.id, name: packageType.name } : undefined,
        unitPrice: form.unitPrice ? Number(form.unitPrice) : 0,
        packagePrice: form.packagePrice ? Number(form.packagePrice) : 0,
        quantityPerPackage: form.quantityPerPackage ? Number(form.quantityPerPackage) : 0,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || t("produto.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("produto.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("produto.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("produto.field.name")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='f7:person' />
                        </span>
                        <input type='text' className='form-control' value={form.name} onChange={setField("name")} required />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("produto.field.baseUnit")}</label>
                      <select className='form-select' value={form.baseUnitId} onChange={setField("baseUnitId")} required>
                        <option value=''>{t("funcionario.select.placeholder")}</option>
                        {baseUnits.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("produto.field.packageType")}</label>
                      <select className='form-select' value={form.packageTypeId} onChange={setField("packageTypeId")}>
                        <option value=''>{t("funcionario.select.placeholder")}</option>
                        {packageTypes.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("produto.field.unitPrice")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:dollar-minimalistic-outline' />
                        </span>
                        <input type='number' min='0' className='form-control' value={form.unitPrice} onChange={setField("unitPrice")} />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("produto.field.packagePrice")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:dollar-minimalistic-outline' />
                        </span>
                        <input type='number' min='0' className='form-control' value={form.packagePrice} onChange={setField("packagePrice")} />
                      </div>
                    </div>
                    <div className='col-sm-3'>
                      <label className='form-label'>{t("produto.field.quantityPerPackage")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:box-minimalistic-outline' />
                        </span>
                        <input type='number' min='0' className='form-control' value={form.quantityPerPackage} onChange={setField("quantityPerPackage")} />
                      </div>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("produto.confirm.description")}</div>
                  <ul className='list-unstyled d-flex flex-column gap-8'>
                    <li>
                      <strong>{t("produto.field.name")}:</strong> {form.name}
                    </li>
                    <li>
                      <strong>{t("produto.field.baseUnit")}:</strong> {unitName(form.baseUnitId)}
                    </li>
                    <li>
                      <strong>{t("produto.field.packageType")}:</strong> {form.packageTypeId ? packageName(form.packageTypeId) : "-"}
                    </li>
                    <li>
                      <strong>{t("produto.field.unitPrice")}:</strong> {form.unitPrice || 0}
                    </li>
                  </ul>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={prevStep}
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-submit btn btn-primary-600 px-32' onClick={handleSubmit} disabled={submitting}>
                      {submitting ? (
                        t("global.loading")
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("produto.success.created")}</h6>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("produto.action.createAnother")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 px-32'
                      onClick={() => navigate("/despesas/produtos")}
                    >
                      <i className='ri-arrow-left-line' /> {t("produto.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarProdutoLayer;
